import React from 'react';
import {useInfiniteFetchTasks} from '@rocket-ui/lk-api';
import {Link} from 'react-router-dom';
import {Task, getTaskCustomer, getTaskPrice, getTaskStatusLabel, getTaskTitle} from 'entities/task';
import {isFreeSmzReceiptError, RECEIPT_ERROR_LABEL} from 'features/order-actions';
import {OrderStatusCell} from './OrderStatusCell';
import {OrderActionCell} from './OrderActionCell';
import {OrdersMobileCard} from './OrdersMobileCard';
import {Table, TableColumn} from 'ui';
import styles from './OrdersTable.module.css';

export function OrdersTable(): React.ReactElement {
	const {data, isLoading, isError, error, hasNextPage, isFetchingNextPage, fetchNextPage} = useInfiniteFetchTasks({
		per_page: 25,
	});

	const tasks = data?.pages.flatMap((page) => page.tasks ?? []) ?? [];
	const loaderRef = React.useRef<HTMLDivElement | null>(null);

	const renderOrderLink = (task: Task, children: React.ReactNode, title?: string) => {
		const orderId = task.id;
		if (orderId == null) return <>{children}</>;

		return (
			<Link
				to={`/orders/${orderId}`}
				className={styles.rowLink}
				aria-label={title ? `Открыть заказ: ${title}` : 'Открыть заказ'}
			>
				{children}
			</Link>
		);
	};

	const columns: TableColumn<Task>[] = [
		{
			key: 'title',
			header: 'Заказ',
			headerClassName: 'text-left',
			cellClassName: styles.titleCell,
			renderCell: (task) => {
				const title = getTaskTitle(task.title, task.type, task.id);

				return renderOrderLink(
					task,
					<span className={styles.clampTwoLines} title={title}>
						{title}
					</span>,
					title,
				);
			},
		},
		{
			key: 'price',
			header: 'Стоимость',
			headerClassName: 'text-right',
			cellClassName: 'text-right',
			renderCell: (task) => {
				const title = getTaskTitle(task.title, task.type, task.id);
				const price = getTaskPrice(task);

				return renderOrderLink(task, <span>{price}</span>, title);
			},
		},
		{
			key: 'customer',
			header: 'Заказчик',
			headerClassName: 'text-left',
			renderCell: (task) => {
				const customer = getTaskCustomer(task);
				const title = getTaskTitle(task.title, task.type, task.id);

				return renderOrderLink(
					task,
					<span className={styles.clampTwoLines} title={customer}>
						{customer}
					</span>,
					title,
				);
			},
		},
		{
			key: 'status',
			header: 'Статус',
			headerClassName: 'text-left',
			renderCell: (task) => {
				if (isFreeSmzReceiptError(task)) {
					const title = getTaskTitle(task.title, task.type, task.id);

					return renderOrderLink(
						task,
						<span className={`${styles.clampTwoLines} ${styles.statusError}`} title={RECEIPT_ERROR_LABEL}>
							{RECEIPT_ERROR_LABEL}
						</span>,
						title,
					);
				}

				const statusLabel = getTaskStatusLabel(task.status);
				const title = getTaskTitle(task.title, task.type, task.id);

				return renderOrderLink(
					task,
					<span className={styles.clampTwoLines} title={statusLabel}>
						<OrderStatusCell task={task} />
					</span>,
					title,
				);
			},
		},
		{
			key: 'action',
			header: null,
			renderCell: (task) => <OrderActionCell task={task} />,
		},
	];

	React.useEffect(() => {
		if (!hasNextPage) return;

		const loaderElement = loaderRef.current;
		if (!loaderElement) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && !isFetchingNextPage) {
					void fetchNextPage();
				}
			},
			{
				root: null,
				rootMargin: '200px 0px',
				threshold: 0.01,
			},
		);

		observer.observe(loaderElement);

		return () => observer.disconnect();
	}, [fetchNextPage, hasNextPage, isFetchingNextPage]);

	if (isLoading) return <p className={styles.stateText}>Загрузка...</p>;

	if (isError) {
		return (
			<p className={styles.errorText} role="alert">
				{error instanceof Error ? error.message : 'Не удалось загрузить заказы'}
			</p>
		);
	}

	if (tasks.length === 0) return <p className={styles.stateText}>Пока нет заказов.</p>;

	return (
		<>
			<div className="hidden sm:block">
				<Table
					data={tasks}
					columns={columns}
					getRowKey={(task, index) => task.id ?? `${task.title ?? 'task'}-${index}`}
					rowClassName="h-[64px] text-base"
					tableClassName={styles.ordersTable}
				/>
			</div>
			<ul className={`sm:hidden ${styles.mobileList}`}>
				{tasks.map((task, index) => (
					<li key={task.id ?? `${task.title ?? 'task'}-${index}`}>
						<OrdersMobileCard task={task} />
					</li>
				))}
			</ul>
			{hasNextPage && (
				<div ref={loaderRef} className={styles.loaderTrigger}>
					{isFetchingNextPage ? 'Загрузка...' : 'Прокрутите ниже для загрузки еще заказов'}
				</div>
			)}
		</>
	);
}
